import type { HistoryEntry, Practice, Routine, Settings } from '../types';
import { defaultSettings } from './timeline';

const KEY = 'yoga.v2';

interface Store {
  settings: Record<string, Partial<Settings>>;
  history: HistoryEntry[];
  migrated: boolean;
  seen: boolean;
  /** Sessions the user made. */
  custom: Routine[];
  /** Edits to built-in sessions (order, items, title). */
  edits: Record<string, { title?: string; items?: string[] }>;
  lastRoutine?: string;
}

const empty = (): Store => ({ settings: {}, history: [], migrated: false, seen: false, custom: [], edits: {} });

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return empty();
    return { ...empty(), ...JSON.parse(raw) };
  } catch {
    return empty();
  }
}

function write(store: Store) {
  try {
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    /* storage full or blocked: settings just won't persist */
  }
}

export function loadSettings(p: Practice): Settings {
  const saved = read().settings[p.id] ?? {};
  const base = defaultSettings(p);
  return { ...base, ...saved, overrides: { ...base.overrides, ...(saved.overrides ?? {}) } };
}

/** True when the user has changed this setting for this practice. */
export const hasSavedSetting = (practiceId: string, key: keyof Settings) => key in (read().settings[practiceId] ?? {});

export function saveSettings(practiceId: string, patch: Partial<Settings>) {
  const store = read();
  store.settings[practiceId] = { ...(store.settings[practiceId] ?? {}), ...patch };
  write(store);
}

export function resetSettings(practiceId: string) {
  const store = read();
  delete store.settings[practiceId];
  write(store);
}

export const loadHistory = (): HistoryEntry[] => read().history;

export function addHistory(entry: HistoryEntry) {
  const store = read();
  store.history.push(entry);
  store.history = store.history.slice(-500);
  write(store);
}

/** A device that has started a practice before skips the welcome page. */
export const isReturning = () => {
  const s = read();
  return s.seen || s.history.length > 0;
};

export function markSeen() {
  const store = read();
  store.seen = true;
  write(store);
}

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;

/** Consecutive days with a completed practice, counting back from today (or yesterday). */
export function currentStreak(history: HistoryEntry[] = loadHistory(), now = new Date()): number {
  const days = new Set(history.filter(h => h.completed).map(h => dayKey(new Date(h.endedAt))));
  let streak = 0;
  const cursor = new Date(now);
  if (!days.has(dayKey(cursor))) cursor.setDate(cursor.getDate() - 1);
  while (days.has(dayKey(cursor))) {
    streak++;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export type TimeBand = 'morning' | 'day' | 'evening';

export function timeBand(now = new Date()): TimeBand {
  const h = now.getHours();
  if (h < 10) return 'morning';
  if (h < 17) return 'day';
  return 'evening';
}

/** Practice completed most often in the current time band, if any. */
export function favouriteForBand(band: TimeBand = timeBand(), history: HistoryEntry[] = loadHistory()): string | null {
  const counts = new Map<string, number>();
  for (const h of history) {
    if (!h.completed) continue;
    if (timeBand(new Date(h.endedAt)) !== band) continue;
    counts.set(h.practiceId, (counts.get(h.practiceId) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  counts.forEach((n, id) => {
    if (n > bestCount) {
      best = id;
      bestCount = n;
    }
  });
  return best;
}

// ---- One-time migration from the old static player (yoga.interval.* and yoga.poseOverrides.v1) ----

const LEGACY_SEQUENCE_TO_PRACTICE: Record<string, string> = {
  'surya-namaskar-session': 'surya-namaskar',
  'padma-sadhana-session': 'padma-sadhana',
  'unified-108-asana-session': '108-asanas',
  '50-asana-session': '50-asanas',
  'yog-nidra': 'yog-nidra',
  'free-practice-session': 'free',
};

/** Defaults the old page loaded for each sequence; a saved value equal to this was never a user choice. */
const LEGACY_DEFAULT_INTERVAL: Record<string, number> = {
  'surya-namaskar': 12,
  'padma-sadhana': 30,
  '108-asanas': 30,
  '50-asanas': 30,
  'yog-nidra': 660,
  free: 120,
};

export function migrateLegacyStorage() {
  const store = read();
  if (store.migrated) return;

  try {
    const overridesRaw = localStorage.getItem('yoga.poseOverrides.v1');
    if (overridesRaw) {
      const parsed = JSON.parse(overridesRaw) as Record<string, { duration?: number; repeat?: number }>;
      for (const [key, value] of Object.entries(parsed)) {
        const [seq, idx] = key.split(':');
        const id = LEGACY_SEQUENCE_TO_PRACTICE[seq];
        if (!id || Number.isNaN(Number(idx))) continue;
        const current = store.settings[id] ?? {};
        store.settings[id] = { ...current, overrides: { ...(current.overrides ?? {}), [Number(idx)]: value } };
      }
    }

    for (const [seq, id] of Object.entries(LEGACY_SEQUENCE_TO_PRACTICE)) {
      const saved = parseInt(localStorage.getItem(`yoga.interval.${seq}`) ?? '', 10);
      if (saved >= 5 && saved <= 1800 && saved !== LEGACY_DEFAULT_INTERVAL[id]) {
        const current = store.settings[id] ?? {};
        store.settings[id] = id === 'free' ? { ...current, bellInterval: saved } : { ...current, secondsPerPose: saved };
      }
      localStorage.removeItem(`yoga.interval.${seq}`);
    }
    localStorage.removeItem('yoga.poseOverrides.v1');
    localStorage.removeItem('theme');
  } catch {
    /* ignore corrupt legacy data */
  }

  store.migrated = true;
  write(store);
}


// ---- Sessions (routines) ----

export const loadRoutineStore = () => {
  const { custom, edits, lastRoutine } = read();
  return { custom, edits, lastRoutine };
};

export function updateRoutineStore(mutate: (s: { custom: Routine[]; edits: Store['edits']; lastRoutine?: string }) => void) {
  const store = read();
  mutate(store);
  write(store);
}
